import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  AddCircleOutline,
  RemoveCircleOutline,
  DoubleArrow,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { themes } from "services/constants";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { infoStyle } from "services/constants";

const timeToMinutes = (timeStr) => {
  if (!timeStr || !timeStr.includes(":")) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
};

const EODCreatePanel = ({ onClose, reCallApi, hasProjectAccess = false }) => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);

  const [formData, setFormData] = useState({
    report_date: dayjs().format("YYYY-MM-DD"),
    total_worked_hours: "",
    planned_tasks_today: [], // Read-only display (Top section)
    achieved_tasks: [], // Achieved Tasks list
    next_session_tasks: [], // Planned for Next Session list
    blockers_notes: "",
  });

  const [projectTaskList, setProjectTaskList] = useState([]);
  const [autoPopulateFailed, setAutoPopulateFailed] = useState(false);

  useEffect(() => {
    if (hasProjectAccess) {
      fetchAutoPopulatedData();
      fetchAvailableTasks();
      fetchPlannedTasksForDate();
    } else {
      // Manual Mode Defaults
      if (formData.achieved_tasks.length === 0) {
        setFormData((prev) => ({
          ...prev,
          achieved_tasks: [{ task_name: "", worked_hours: "" }],
        }));
      }
      if (formData.next_session_tasks.length === 0) {
        setFormData((prev) => ({
          ...prev,
          next_session_tasks: [{ task_name: "", sequence_order: 1 }],
        }));
      }
      setFormData((prev) => ({ ...prev, planned_tasks_today: [] }));
    }
    // eslint-disable-next-line
  }, [hasProjectAccess]);

  const fetchAutoPopulatedData = async () => {
    setAutoPopulateFailed(false); // clear in case we call again with new date
    try {
      const res = await customAPI(
        `${domain}eod-reports/auto_populate_achieved/`,
        "POST",
        { user_id: userDetails.id, report_date: formData.report_date },
      );
      if (res.ok) {
        const achieved = res.responseDetails.achieved_tasks || [];

        setFormData((prev) => ({
          ...prev,
          total_worked_hours: res.responseDetails.total_worked_hours || "00:00",
          achieved_tasks: achieved.map((t) => ({
            task_id: t.task_id,
            task_no: t.task_no || "-",
            task_name: t.task_name || "",
            worked_hours: t.worked_hours || "00:00",
          })),
        }));
      } else {
        // disabling submit when a 400 is returned from auto-populate
        if (res.code === 400) setAutoPopulateFailed(true);
      }
    } catch (error) {
      console.error("Error fetching auto-populated data", error);
      setAutoPopulateFailed(true);
    }
  };

  const isValidTime = (timeStr) => {
    if (!timeStr || timeStr === "Invalid Date") return false;
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(timeStr);
  };

  const fetchPlannedTasksForDate = async () => {
    if (!userDetails?.id) return;
    try {
      const dateParam = formData.report_date;

      const res = await customGetAPI(
        `${domain}eod-reports/planned_for_date/?user_id=${userDetails.id}&date=${dateParam}`,
      );

      const responseData = res.responseDetails || res.data || res;

      if (res.ok || (responseData && responseData.planned_tasks)) {
        const tasks = responseData.planned_tasks || [];

        setFormData((prev) => ({
          ...prev,
          planned_tasks_today: tasks.map((t) => ({
            task_id: t.task_id,
            task_no: t.task_no,
            task_name: t.task_name,
            planned_hours: t.planned_hours
              ? t.planned_hours.substring(0, 5)
              : "-",
            remaining_hours: t.remaining_eta || "-",
            worked_hours: "-",
          })),
        }));
      }
    } catch (error) {
      console.error("Error fetching planned tasks for date", error);
    }
  };

  const fetchAvailableTasks = async () => {
    try {
      const res = await customGetAPI(
        `${domain}eod-reports/available_tasks/?user_id=${userDetails.id}`,
      );
      if (res.ok) {
        const tasks = Array.isArray(res.responseDetails)
          ? res.responseDetails
          : res.data || [];
        setProjectTaskList(
          tasks.map((t) => ({
            task_id: t.id,
            task_no: t.task_code,
            task_name: t.taskname,
            remaining_eta: t.remaining_eta || "00:00",
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching available tasks", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateRow = (listName, index, field, value) => {
    const newList = [...formData[listName]];

    if (listName === "next_session_tasks" && field === "planned_hours") {
      const task = newList[index];
      if (hasProjectAccess && task.remaining_eta && value.length === 5) {
        if (timeToMinutes(value) > timeToMinutes(task.remaining_eta)) {
          showToast(
            "error",
            `Cannot exceed Remaining ETA (${task.remaining_eta})`,
          );
          return;
        }
      }
    }

    newList[index][field] = value;
    setFormData((prev) => ({ ...prev, [listName]: newList }));
  };

  const removeRow = (listName, index) => {
    const newList = [...formData[listName]];
    newList.splice(index, 1);
    setFormData((prev) => ({ ...prev, [listName]: newList }));
  };

  const addRow = (listName) => {
    const newItem =
      listName === "achieved_tasks"
        ? { task_name: "", worked_hours: "" }
        : {
            task_id: null,
            task_no: "",
            task_name: "",
            remaining_eta: "",
            planned_hours: "",
          };

    setFormData((prev) => ({
      ...prev,
      [listName]: [...prev[listName], newItem],
    }));
  };

  const canAddNextRow = (list, type) => {
    if (list.length === 0) return true;
    const lastItem = list[list.length - 1];

    if (!lastItem.task_name || !lastItem.task_name.trim()) return false;

    if (type === "project_planned") {
      if (!lastItem.task_id) return false;
      if (!isValidTime(lastItem.planned_hours)) return false;
      if (!lastItem.planned_hours || lastItem.planned_hours.length < 5)
        return false;
    }
    return true;
  };

  const isSubmitDisabled = () => {
    if (autoPopulateFailed) return true;
    if (timeToMinutes(formData.total_worked_hours) > timeToMinutes("17:00")) {
      return true;
    }
    if (!hasProjectAccess) {
      if (
        !formData.total_worked_hours ||
        formData.total_worked_hours === "Invalid Date"
      )
        return true;
      if (formData.achieved_tasks.length === 0) return true;
      if (formData.achieved_tasks.some((t) => !t.task_name.trim())) return true;
    }

    if (formData.next_session_tasks.length === 0) return true;

    if (hasProjectAccess) {
      if (
        formData.next_session_tasks.some(
          (t) =>
            !t.task_id ||
            !isValidTime(t.planned_hours) ||
            !t.planned_hours ||
            t.planned_hours.length < 5,
        )
      )
        return true;
      const hasExceededTime = formData.next_session_tasks.some(
        (t) =>
          t.remaining_eta &&
          timeToMinutes(t.planned_hours) > timeToMinutes(t.remaining_eta),
      );
      if (hasExceededTime) return true;
    } else {
      if (formData.next_session_tasks.some((t) => !t.task_name.trim()))
        return true;
    }

    return false;
  };

  const handleSubmit = async () => {
    let payload = {
      user_id: userDetails.id,
      report_date: formData.report_date,
      has_project_access: hasProjectAccess,
      blockers_notes: formData.blockers_notes,
    };

    if (hasProjectAccess) {
      payload.planned_tasks = formData.next_session_tasks.map((t, index) => ({
        task_id: t.task_id,
        planned_hours:
          t.planned_hours.length === 5
            ? `${t.planned_hours}:00`
            : t.planned_hours,
        sequence_order: index + 1,
      }));
    } else {
      payload.total_worked_hours = formData.total_worked_hours;
      payload.achieved_tasks = formData.achieved_tasks.map((t) => ({
        task_name: t.task_name,
      }));
      payload.planned_tasks = formData.next_session_tasks.map((t, index) => ({
        task_name: t.task_name,
        sequence_order: index + 1,
      }));
    }

    try {
      const res = await customAPI(`${domain}eod-reports/`, "POST", payload);
      if (res.ok) {
        showToast("success", "EOD Report Submitted Successfully");
        reCallApi(true);
        onClose();
      } else {
        showToast("error", res.message || "Failed to submit report");
      }
    } catch (err) {
      console.error(err);
      showToast("error", "Error submitting report");
    }
  };

  const styles = {
    manualBox: (type) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      p: 0.5,
      mb: 1.5,
      borderRadius: "8px",
      backgroundColor: type === "planned" ? "#FFF9E6" : "#F1FDF9",
      border: `1px solid ${type === "planned" ? "#FFD700" : "#00D4AA"}`,
    }),
    boxInput: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
        "& fieldset": { border: "none" },
        "&:hover fieldset": { border: "none" },
        "&.Mui-focused fieldset": { border: "none" },
      },
      "& .MuiOutlinedInput-input": { padding: "10px", fontSize: "0.95rem" },
    },
    tableHead: (bgcolor) => ({
      backgroundColor: bgcolor,
      "& th": {
        fontWeight: "bold",
        fontSize: "0.8rem",
        color: "#333",
        borderBottom: "1px solid #ddd",
        whiteSpace: "nowrap",
        py: 1,
        px: 1,
      },
    }),
    selectInput: {
      "& .MuiOutlinedInput-root": {
        paddingTop: "2px !important",
        paddingBottom: "2px !important",
        paddingLeft: "8px !important",
        "& fieldset": { borderColor: "#c4c4c4" },
        "&:hover fieldset": { borderColor: "#000" },
      },
      "& .MuiOutlinedInput-input": {
        fontSize: "0.85rem",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "#fff",
        }}
      >
        {/* HEADER */}

        <Paper
          elevation={0}
          sx={{
            borderBottom: `1px solid ${themes?.miliedLightGray}`,
            borderRadius: 0,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <Box sx={{ ml: 1 }}>
                <IconButton
                  onClick={onClose}
                  sx={{
                    color: themes?.grayColor,
                    "&:hover": {
                      backgroundColor: themes?.whiteColor,
                    },
                  }}
                >
                  <DoubleArrow />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ ml: 2.2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Create EOD Report
              </Typography>
              <Typography {...infoStyle}>
                Create your daily work progress
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          {/* EMPLOYEE INFO */}
          <Box sx={{ mb: 2 }}>
            <UserDetailsRow
              userDetails={{
                image_url: userDetails?.image_url,
                employee_id: userDetails?.employee_id,
                user_name: userDetails?.first_name,
                department_name: userDetails?.department_name,
                designation: userDetails?.designation,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="body2">
              <strong>Date:</strong>{" "}
              {dayjs(formData.report_date).format("DD.MM.YYYY")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">
                <strong>Worked Hours:</strong>
              </Typography>
              <TimePicker
                value={
                  formData.total_worked_hours
                    ? dayjs(formData.total_worked_hours, "HH:mm")
                    : null
                }
                onChange={(newValue) => {
                  const timeStr = newValue ? newValue.format("HH:mm") : "";

                  const [hh, mm] = timeStr.split(":").map(Number);
                  if (hh > 17 || (hh === 17 && mm > 0)) {
                    showToast("error", "Max allowed time is 17:00");
                  }

                  handleChange("total_worked_hours", timeStr);
                }}
                ampm={false}
                readOnly={hasProjectAccess}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "HH:MM",
                    sx: {
                      width: 120,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        height: 40,
                        bgcolor: hasProjectAccess ? "#f5f5f5" : "#fff",
                      },
                    },
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Planned Tasks
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              The tasks you planned to complete today.
            </Typography>

            {hasProjectAccess ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead sx={styles.tableHead("#FFF9E6")}>
                    <TableRow>
                      <TableCell>Task No</TableCell>
                      <TableCell>Task Name</TableCell>
                      <TableCell>Planned hrs</TableCell>
                      <TableCell>Worked hrs</TableCell>
                      <TableCell>Remaining hrs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.planned_tasks_today.length > 0 ? (
                      formData.planned_tasks_today.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{row.task_no}</TableCell>
                          <TableCell>{row.task_name}</TableCell>
                          <TableCell>{row.planned_hours}</TableCell>
                          <TableCell>{row.worked_hours}</TableCell>
                          <TableCell>{row.remaining_hours}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{
                            py: 3,
                            color: "text.secondary",
                            fontStyle: "italic",
                          }}
                        >
                          No planned tasks found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : formData.planned_tasks_today.length > 0 ? (
              formData.planned_tasks_today.map((task, i) => (
                <Box key={i} sx={styles.manualBox("planned")}>
                  <Typography
                    variant="body2"
                    sx={{ px: 2, py: 1, fontWeight: 500 }}
                  >
                    {task.task_name}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                No planned tasks found
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Achieved Tasks
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              List down the tasks you actually completed today.
            </Typography>

            {hasProjectAccess ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead sx={styles.tableHead("#F1FDF9")}>
                    <TableRow>
                      <TableCell width="15%">Task No</TableCell>
                      <TableCell width="65%">Task Name</TableCell>
                      <TableCell width="20%">Worked Hrs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.achieved_tasks.length > 0 ? (
                      formData.achieved_tasks.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.task_no}</TableCell>
                          <TableCell>{row.task_name}</TableCell>
                          <TableCell>{row.worked_hours}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{
                            py: 3,
                            color: "text.secondary",
                            fontStyle: "italic",
                          }}
                        >
                          No achieved tasks found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box>
                {formData.achieved_tasks.map((row, index) => (
                  <Box key={index} sx={styles.manualBox("achieved")}>
                    <TextField
                      fullWidth
                      value={row.task_name}
                      onChange={(e) =>
                        updateRow(
                          "achieved_tasks",
                          index,
                          "task_name",
                          e.target.value,
                        )
                      }
                      placeholder="Enter achieved task..."
                      sx={styles.boxInput}
                    />
                    {index > 0 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow("achieved_tasks", index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {canAddNextRow(formData.achieved_tasks, "manual_achieved") && (
                  <IconButton
                    onClick={() => addRow("achieved_tasks")}
                    sx={{ color: "#00D4AA" }}
                  >
                    <AddCircleOutline />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Planned for Next Session
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              Add the tasks you intend to work on in the next session.
            </Typography>

            {hasProjectAccess ? (
              <Box>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  <Table size="small">
                    <TableHead sx={styles.tableHead("#FFF9E6")}>
                      <TableRow>
                        <TableCell width="15%">Task No</TableCell>
                        <TableCell width="50%">Task Name</TableCell>
                        <TableCell width="15%">Remaining ETA</TableCell>
                        <TableCell width="15%">Planned</TableCell>
                        <TableCell width="5%"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.next_session_tasks.length > 0 ? (
                        formData.next_session_tasks.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={projectTaskList.filter(
                                  (task) =>
                                    !formData.next_session_tasks.some(
                                      (selected) =>
                                        selected.task_id === task.task_id,
                                    ) || task.task_id === row.task_id,
                                )}
                                getOptionLabel={(option) =>
                                  option.task_no || ""
                                }
                                // Important: Bind the value so it syncs with Task Name
                                value={
                                  projectTaskList.find(
                                    (t) => t.task_id === row.task_id,
                                  ) || null
                                }
                                onChange={(e, val) => {
                                  const updatedList = [
                                    ...formData.next_session_tasks,
                                  ];
                                  if (val) {
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      task_id: val.task_id,
                                      task_no: val.task_no,
                                      task_name: val.task_name,
                                      remaining_eta: val.remaining_eta,
                                    };
                                  } else {
                                    // Handle clear
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      task_id: null,
                                      task_no: "",
                                      task_name: "",
                                      remaining_eta: "",
                                    };
                                  }
                                  setFormData({
                                    ...formData,
                                    next_session_tasks: updatedList,
                                  });
                                }}
                                renderInput={(params) => (
                                  <Tooltip
                                    title={row.task_no || ""}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <TextField
                                      {...params}
                                      placeholder="Select No"
                                      sx={{
                                        ...styles.selectInput,
                                        minWidth: "110px",
                                        "& .MuiOutlinedInput-input": {
                                          fontSize: "0.85rem",
                                          textOverflow: "clip",
                                          overflow: "visible",
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={row.task_name || ""}
                                arrow
                                slotProps={tooltipStyleProps}
                              >
                                <Autocomplete
                                  size="small"
                                  options={projectTaskList.filter(
                                    (task) =>
                                      !formData.next_session_tasks.some(
                                        (selected) =>
                                          selected.task_id === task.task_id,
                                      ) || task.task_id === row.task_id,
                                  )}
                                  getOptionLabel={(option) =>
                                    option.task_name || ""
                                  }
                                  // Important: Bind the value so it syncs with Task No
                                  value={
                                    projectTaskList.find(
                                      (t) => t.task_id === row.task_id,
                                    ) || null
                                  }
                                  onChange={(e, val) => {
                                    const updatedList = [
                                      ...formData.next_session_tasks,
                                    ];
                                    if (val) {
                                      updatedList[index] = {
                                        ...updatedList[index],
                                        task_id: val.task_id,
                                        task_no: val.task_no,
                                        task_name: val.task_name,
                                        remaining_eta: val.remaining_eta,
                                      };
                                    } else {
                                      updatedList[index] = {
                                        ...updatedList[index],
                                        task_id: null,
                                        task_no: "",
                                        task_name: "",
                                        remaining_eta: "",
                                      };
                                    }
                                    setFormData({
                                      ...formData,
                                      next_session_tasks: updatedList,
                                    });
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select Name"
                                      sx={styles.selectInput}
                                    />
                                  )}
                                />
                              </Tooltip>
                            </TableCell>

                            <TableCell>{row.remaining_eta || "-"}</TableCell>
                            <TableCell>
                              <TimePicker
                                value={
                                  row.planned_hours
                                    ? dayjs(row.planned_hours, "HH:mm")
                                    : null
                                }
                                onChange={(newValue) => {
                                  const timeStr = newValue
                                    ? newValue.format("HH:mm")
                                    : "";

                                  const inputMinutes = timeToMinutes(timeStr);
                                  const etaMinutes = row.remaining_eta
                                    ? timeToMinutes(row.remaining_eta)
                                    : Infinity;

                                  if (
                                    row.remaining_eta &&
                                    inputMinutes > etaMinutes
                                  ) {
                                    showToast(
                                      "error",
                                      `Cannot exceed Remaining ETA (${row.remaining_eta}). Resetting to max.`,
                                    );

                                    updateRow(
                                      "next_session_tasks",
                                      index,
                                      "planned_hours",
                                      row.remaining_eta,
                                    );
                                  } else {
                                    updateRow(
                                      "next_session_tasks",
                                      index,
                                      "planned_hours",
                                      timeStr,
                                    );
                                  }
                                }}
                                ampm={false}
                                disabled={!row.task_id}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    sx: { width: 110 },
                                    error:
                                      row.remaining_eta &&
                                      timeToMinutes(row.planned_hours) >
                                        timeToMinutes(row.remaining_eta),
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {index > 0 && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    removeRow("next_session_tasks", index)
                                  }
                                >
                                  <RemoveCircleOutline fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{
                              py: 3,
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            Add a task to plan for next session
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {canAddNextRow(
                  formData.next_session_tasks,
                  "project_planned",
                ) && (
                  <IconButton
                    onClick={() => addRow("next_session_tasks")}
                    sx={{ mt: 1, color: "#FFD700" }}
                  >
                    <AddCircleOutline />
                  </IconButton>
                )}
              </Box>
            ) : (
              <Box>
                {formData.next_session_tasks.map((row, index) => (
                  <Box key={index} sx={styles.manualBox("planned")}>
                    <TextField
                      fullWidth
                      value={row.task_name}
                      onChange={(e) =>
                        updateRow(
                          "next_session_tasks",
                          index,
                          "task_name",
                          e.target.value,
                        )
                      }
                      placeholder="Enter planned task..."
                      sx={styles.boxInput}
                    />
                    {index > 0 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow("next_session_tasks", index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {canAddNextRow(
                  formData.next_session_tasks,
                  "manual_planned",
                ) && (
                  <IconButton
                    onClick={() => addRow("next_session_tasks")}
                    sx={{ color: "#FFD700" }}
                  >
                    <AddCircleOutline />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Blockers / Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add blockers..."
              value={formData.blockers_notes}
              onChange={(e) => handleChange("blockers_notes", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #eee",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          {autoPopulateFailed && (
            <Typography variant="caption" color="error" sx={{ mb: 1 }}>
              Auto-populate failed, submit disabled until resolved.
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled() || autoPopulateFailed}
            sx={{
              bgcolor: "#00C853",
              textTransform: "none",
              fontWeight: "bold",
              px: 4,
              "&:hover": { bgcolor: "#00E676" },
              "&:disabled": { bgcolor: "#ccc", color: "#666" },
            }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default EODCreatePanel;
